/**
 * Traduction UI à partir de libellés FR (ou clés i18n).
 * Permet de traduire formulaires / tableaux sans tout réécrire d’un coup.
 *
 * Usage :
 *   const { t, ui } = useI18n();
 *   ui("FOURNISSEUR")  → "Supplier" (si EN)
 *   t("fields.fournisseur") // clé directe
 */
import { useMemo } from "react";
import { useI18n } from "./I18nContext";

function stripAccents(s) {
  return String(s)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function normalizeLabel(s) {
  return stripAccents(String(s || ""))
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ")
    .replace(/[…]/g, "...")
    .replace(/[—–−]/g, "-");
}

/** Libellés FR courants → clé i18n (common.* / fields.* / …) */
const UI_LOOKUP = {
  // common
  LANGUE: "common.language",
  DECONNEXION: "common.logout",
  RECHERCHER: "common.search",
  MENU: "common.menu",
  CONFIGURATION: "common.config",
  FERMER: "common.close",
  "CHARGEMENT...": "common.loading",
  "CHARGEMENT…": "common.loading",
  CHARGEMENT: "common.loading",
  ENREGISTRER: "common.save",
  "ENREGISTREMENT...": "common.saving",
  "ENREGISTREMENT…": "common.saving",
  MODIFIER: "common.update",
  "MISE A JOUR...": "common.updating",
  "MISE À JOUR…": "common.updating",
  ANNULER: "common.cancel",
  RETOUR: "common.back",
  "RETOUR MENU": "common.backMenu",
  ERREUR: "common.error",
  "ERREUR SERVEUR": "common.errorServer",
  AJOUTER: "common.add",
  SUPPRIMER: "common.delete",
  CONFIRMER: "common.confirm",
  VALIDER: "common.validate",
  EXPORTER: "common.export",
  "EXPORT CSV": "common.exportCsv",
  IMPRIMER: "common.print",
  TELECHARGER: "common.download",
  ACTIONS: "common.actions",
  AUCUN: "common.none",
  "- SELECTIONNER -": "common.select",
  "— SELECTIONNER —": "common.select",
  "— SÉLECTIONNER —": "common.select",
  "TRIER PAR": "common.sortBy",
  "FILTRER PAR": "common.filterBy",
  FILTRER: "common.filterBy",
  TRIER: "common.sortBy",
  ET: "common.and",
  "- COLONNE -": "common.column",
  "— COLONNE —": "common.column",
  "- VALEUR -": "common.value",
  "— VALEUR —": "common.value",
  "LIGNES PAR PAGE": "common.rowsPerPage",
  REINITIALISER: "common.reset",
  "RECALCULER STOCK": "common.recalcStock",
  "AUCUNE LIGNE": "common.noRows",
  AFFICHAGE: "common.displaying",
  SUR: "common.of",
  RESULTATS: "common.results",
  "SESSION EXPIREE.": "common.sessionExpired",
  "SESSION EXPIRÉE.": "common.sessionExpired",
  ASC: "common.asc",
  DESC: "common.desc",
  OUI: "common.yes",
  NON: "common.no",
  OPERATEUR: "common.operator",
  "OPÉRATEUR": "common.operator",
  OK: "common.ok",
  ENVOYER: "common.send",
  SUCCES: "common.success",
  SUCCÈS: "common.success",
  "VOIR PDF": "common.seePdf",
  REMPLACER: "common.replace",
  "TOUT DESELECTIONNER": "common.deselectAll",
  CLAIR: "common.light",
  SOMBRE: "common.dark",

  // fields
  INDEX: "fields.index",
  DATE: "fields.date",
  "DATE DE RECEPTION": "fields.dateReception",
  "DATE RECEPTION": "fields.dateReception",
  "DATE EN COURS": "fields.dateEnCours",
  "DATE FINI": "fields.dateFini",
  "DATE DE PEREMPTION": "fields.datePeremption",
  MASSE: "fields.masse",
  "MASSE (KG)": "fields.masseKg",
  "MASSE (G)": "fields.masseG",
  "QUANTITE RESTANTE": "fields.masseRestante",
  "QUANTITÉ RESTANTE": "fields.masseRestante",
  FOURNISSEUR: "fields.fournisseur",
  ETAT: "fields.etat",
  ÉTAT: "fields.etat",
  STATUT: "fields.statut",
  "PRIX/KG (€)": "fields.prixKg",
  "PRIX TOTAL (€)": "fields.prixTotal",
  "PRIX TOTALES (€)": "fields.prixTotal",
  TEMPERATURE: "fields.temperature",
  "TEMPÉRATURE": "fields.temperature",
  HUMIDITE: "fields.humidite",
  "HUMIDITÉ": "fields.humidite",
  EPAISSEUR: "fields.epaisseur",
  "ÉPAISSEUR": "fields.epaisseur",
  "EPAISSEUR (MM)": "fields.epaisseurMm",
  "ÉPAISSEUR (MM)": "fields.epaisseurMm",
  LONGUEUR: "fields.longueur",
  "LONGUEUR (MM)": "fields.longueurMm",
  LARGEUR: "fields.largeur",
  "LARGEUR (MM)": "fields.largeurMm",
  COMMENTAIRE: "fields.commentaire",
  "NUM LOT WOODOO": "fields.numLot",
  "NUMÉRO DU LOT FOURNISSEUR": "fields.refFournisseur",
  "NB PLACAGE": "fields.nbPlacage",
  "NB PLACAGE RESTANT": "fields.nbPlacageRestant",
  "NOMBRE DE PLACAGE RESTANT": "fields.nbPlacageRestant",
  "QUANTITE NOK": "fields.quantiteNok",
  "QUANTITÉ NOK": "fields.quantiteNok",
  CERTIF: "fields.certif",
  "CERTIFICAT PDF": "fields.certifPdf",
  "REFERENCE FOURNISSEUR": "fields.refFournisseur",
  "RÉFÉRENCE FOURNISSEUR": "fields.refFournisseur",
  "NO BATCH": "fields.noBatch",
  "NUM BIDON": "fields.numBidon",
  ESSENCE: "fields.essence",
  COULEUR: "fields.couleur",
  COLORIS: "fields.couleur",
  COUPE: "fields.coupe",
  QUALITE: "fields.qualite",
  "QUALITÉ": "fields.qualite",
  DUREE: "fields.duree",
  "DURÉE": "fields.duree",
  "GAIN MASSIQUE": "fields.gainMassique",
  PN: "fields.pn",
  "PLI PN": "fields.plyPn",
  "STACK ID": "fields.stackId",
  "ID STACK": "fields.stackId",
  "QUANTITE TOTALE DE LOT": "fields.qtyLot",
  "QUANTITÉ TOTALE DE LOT": "fields.qtyLot",
  "QUANTITE TOTALE": "fields.qtyTotal",
  "QUANTITÉ TOTALE": "fields.qtyTotal",
  "NOMBRE DE LOT": "fields.nbLot",
  CONDITIONNEMENT: "fields.conditionnement",
  "SOLID CONTENT": "fields.solidContent",

  // stock
  "SUIVI DE STOCK": "stock.titleBois",
  "SUIVI DE STOCK LIANT": "stock.titleResine",
  "SUIVI DE STOCK RESINE": "stock.titleResine",
  "SUIVI DE STOCK AMORCEUR": "stock.titleAmorceur",
  "LOT NEUF": "stock.lotNeuf",
  "LISTE DES BIDONS": "stock.listeBidons",
  "PARAMETRES DE RECEPTION BOIS": "stock.paramsBois",
  "PARAMÈTRES DE RÉCEPTION BOIS": "stock.paramsBois",
  "SUIVI DE LIVRAISON": "stock.suiviLivraison",
  LOGISTIQUE: "nav.logistique",

  // admin / options
  COULEURS: "admin.couleurs",
  COUPES: "admin.coupes",
  ESSENCES: "admin.essences",
  FOURNISSEURS: "admin.fournisseurs",
  "DIMENSIONS AMORCEUR": "admin.dimAmorceur",
};

// Also register lowercase / title-case variants via normalizeLabel
export function makeUiTranslator(t) {
  return function ui(text, fallback) {
    if (text == null || text === "") return text ?? "";
    const raw = String(text).replace(/\s*:\s*$/, "").trim(); // "Filtrer par :" → "Filtrer par"
    const norm = normalizeLabel(raw);
    const key =
      UI_LOOKUP[norm] ||
      UI_LOOKUP[raw] ||
      UI_LOOKUP[stripAccents(raw).toUpperCase()];
    if (key) return t(key, fallback != null ? fallback : raw);
    return fallback != null ? fallback : String(text);
  };
}

export function useUi() {
  const { t, lang, setLang, locales } = useI18n();
  const ui = useMemo(() => makeUiTranslator(t), [t]);
  return { t, ui, lang, setLang, locales };
}

export { normalizeLabel, UI_LOOKUP };
